import { useEffect } from "react";

const WRAPPER_SELECTOR = ".ant-notification-notice-wrapper";
const NOTICE_SELECTOR = ".ant-notification-notice";
const CLOSE_SELECTOR = ".ant-notification-notice-close";
// Movement before a press turns into a drag, so clicks on links/close still work
const DRAG_START_PX = 6;
// Dismiss when dragged past this share of the notice width...
const DISMISS_RATIO = 0.3;
// ...or flicked faster than this (px/ms)
const DISMISS_VELOCITY = 0.5;
const SNAP_MS = 200;

/**
 * Lets the user drag antd notifications off the screen edge they sit on
 * (right for the default topRight placement). Dismissal goes through the
 * notice's own close button so onClose handlers still run.
 */
const useSwipeToDismiss = () => {
	useEffect(() => {
		const onPointerDown = (downEvent: PointerEvent) => {
			if (downEvent.button !== 0) return;
			// antd 5 paints the white panel on the wrapper, so move that
			// (falling back to the notice itself for builds without a wrapper)
			const target = downEvent.target as Element | null;
			const notice =
				target?.closest<HTMLElement>(WRAPPER_SELECTOR) ??
				target?.closest<HTMLElement>(NOTICE_SELECTOR);
			if (!notice) return;

			const container = notice.closest(".ant-notification");
			const direction = container?.className.match(
				/ant-notification-(topLeft|bottomLeft)/,
			)
				? -1
				: 1;

			const startX = downEvent.clientX;
			const startY = downEvent.clientY;
			let dragging = false;
			let lastX = startX;
			let lastT = downEvent.timeStamp;
			let velocity = 0;

			const offsetFor = (dx: number) =>
				// Full movement towards the edge, rubber-band the other way
				dx * direction >= 0 ? dx : dx / 4;

			const onMove = (e: PointerEvent) => {
				if (e.pointerId !== downEvent.pointerId) return;
				const dx = e.clientX - startX;
				if (!dragging) {
					if (Math.abs(dx) < DRAG_START_PX) return;
					// Mostly vertical: leave it to scrolling / text selection
					if (Math.abs(e.clientY - startY) > Math.abs(dx)) {
						cleanup();
						return;
					}
					dragging = true;
					notice.setPointerCapture?.(e.pointerId);
					notice.style.transition = "none";
					notice.style.userSelect = "none";
					window.getSelection()?.removeAllRanges();
				}
				e.preventDefault();
				const dt = e.timeStamp - lastT;
				if (dt > 0) velocity = (e.clientX - lastX) / dt;
				lastX = e.clientX;
				lastT = e.timeStamp;

				const offset = offsetFor(dx);
				const progress = Math.min(
					Math.abs(offset) / notice.offsetWidth,
					1,
				);
				notice.style.transform = `translateX(${offset}px)`;
				notice.style.opacity = String(
					offset * direction > 0 ? 1 - progress * 0.6 : 1,
				);
			};

			const onUp = (e: PointerEvent) => {
				if (e.pointerId !== downEvent.pointerId) return;
				cleanup();
				if (!dragging) return;

				// Swallow the click that follows the drag (if the browser fires one)
				const swallowClick = (clickEvent: MouseEvent) => {
					clickEvent.stopPropagation();
					clickEvent.preventDefault();
				};
				window.addEventListener("click", swallowClick, {
					capture: true,
					once: true,
				});
				setTimeout(() => {
					window.removeEventListener("click", swallowClick, true);
					notice.style.removeProperty("user-select");
				}, 50);

				const offset = offsetFor(e.clientX - startX);
				const dismiss =
					e.type === "pointerup" &&
					(offset * direction > notice.offsetWidth * DISMISS_RATIO ||
						velocity * direction > DISMISS_VELOCITY);

				notice.style.transition = `transform ${SNAP_MS}ms ease-out, opacity ${SNAP_MS}ms ease-out`;
				if (dismiss) {
					notice.style.transform = `translateX(${
						direction * (notice.offsetWidth + 40)
					}px)`;
					notice.style.opacity = "0";
					setTimeout(() => {
						notice
							.querySelector<HTMLElement>(CLOSE_SELECTOR)
							?.click();
					}, SNAP_MS);
				} else {
					notice.style.transform = "";
					notice.style.opacity = "";
				}
			};

			const cleanup = () => {
				window.removeEventListener("pointermove", onMove);
				window.removeEventListener("pointerup", onUp);
				window.removeEventListener("pointercancel", onUp);
			};

			window.addEventListener("pointermove", onMove, { passive: false });
			window.addEventListener("pointerup", onUp);
			window.addEventListener("pointercancel", onUp);
		};

		document.addEventListener("pointerdown", onPointerDown);
		return () => document.removeEventListener("pointerdown", onPointerDown);
	}, []);
};

export default useSwipeToDismiss;
